import React, { useEffect, useState } from "react";
import { createPageUrl } from "@/utils";
import { CheckCircle2, ArrowRight, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PLAN_LABELS } from "@/components/shared/PlanGuard";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SubscriptionSuccess() {
  const urlParams = new URLSearchParams(window.location.search);
  const plan = urlParams.get("plan");
  const venueId = urlParams.get("venue_id");
  const sessionId = urlParams.get("session_id");
  const [status, setStatus] = useState("checking"); // checking, success, error
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    } else {
      setStatus("success");
    }
  }, [sessionId]);

  useEffect(() => {
    if (status !== "success") return;
    const timer = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          clearInterval(timer);
          window.location.href = createPageUrl("Dashboard");
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [status]);

  const pollPaymentStatus = async (sid, attempts) => {
    const maxAttempts = 8;
    const pollInterval = 2500;

    if (attempts >= maxAttempts) {
      setStatus("success"); // Assume success after max attempts
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/checkout/status/${sid}`, {
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Status check failed');

      const data = await response.json();

      if (data.payment_status === "paid") {
        setStatus("success");
        return;
      } else if (data.status === "expired") {
        setStatus("error");
        return;
      }

      // Still pending, keep polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (err) {
      console.error("Poll error:", err);
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-10 text-center" data-testid="subscription-success-card">
        {status === "checking" && (
          <>
            <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verificant pagament...</h1>
            <p className="text-gray-500">Estem confirmant el teu pagament amb Stripe.</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-testid="payment-confirmed-title">Pagament confirmat!</h1>
            <p className="text-gray-500 mb-4">
              La teva subscripció al pla <strong className="text-[#7B2D3F]">{PLAN_LABELS[plan] || plan}</strong> s'ha activat correctament.
            </p>
            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-sm text-gray-600">
              <p>Ja tens accés a tots els mòduls del pla {PLAN_LABELS[plan] || plan}.</p>
            </div>
            <Button
              onClick={() => window.location.href = createPageUrl("Dashboard")}
              className="w-full bg-[#7B2D3F] hover:bg-[#5B1D2F] text-white rounded-xl h-11 font-semibold"
              data-testid="go-to-dashboard-btn"
            >
              Anar al Dashboard <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <p className="text-xs text-gray-400 mt-4">
              Redirigint automàticament en {countdown}s...
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Error en el pagament</h1>
            <p className="text-gray-500 mb-4">
              No s'ha pogut confirmar el pagament. Si us plau, torna-ho a provar.
            </p>
            <Button
              onClick={() => window.location.href = createPageUrl("SelectPlan") + `?venue_id=${venueId}`}
              className="w-full bg-[#7B2D3F] hover:bg-[#5B1D2F] text-white rounded-xl h-11 font-semibold"
              data-testid="retry-payment-btn"
            >
              Tornar a escollir pla
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
